/**
 * Session slice.
 *
 * Authenticated user, subscription plan, and AI credit usage.
 *
 * Slices share one store: `set` and `get` are typed over the whole AppStore,
 * so an action here can read or write another slice's state where the domains
 * genuinely interact (loading a project resets the tree, converting a triage
 * item calls the tree's addNode).
 */
import type { StateCreator } from 'zustand';
import { fetchAiUsage } from '../../api/ai';
import { fetchSubscriptionStatus } from '../../api/lemonSqueezy';
import type { UserModel } from '../../types';
import type { AppStore } from '../storeTypes';

export const createSessionSlice: StateCreator<
    AppStore,
    [],
    [],
    Pick<
        AppStore,
        | 'user'
        | 'setAuth'
        | 'clearAuth'
        | 'refreshSubscription'
        | 'aiUsage'
        | 'refreshAiUsage'
    >
> = (set, get) => ({
    // JWT is stored exclusively in the httpOnly `zt_token` cookie — never in localStorage.
    user: JSON.parse(localStorage.getItem('ost_user') || 'null') as UserModel | null,

    setAuth: (user: UserModel) => {
        // Persist user metadata (non-sensitive) for fast UI restore on page load.
        // The JWT itself lives only in the httpOnly cookie set by the server.
        localStorage.setItem('ost_user', JSON.stringify(user));
        set({ user });
    },

    clearAuth: () => {
        localStorage.removeItem('ost_user');
        localStorage.removeItem('ost_active_project_id');
        set({ user: null, projects: [], activeProject: null, aiUsage: null });
    },

    refreshSubscription: async () => {
        const { user } = get();
        if (!user) return;
        try {
            const status = await fetchSubscriptionStatus();
            // Re-check after the await: the user may have logged out while the
            // request was in-flight. Writing back here would restore a null user
            // and re-trigger auth effects, causing spurious 401 requests.
            if (!get().user) return;
            // Only update the store if plan or userCategory changed. Writing a new
            // object reference unconditionally (even with the same values) would
            // change `user` in the store, re-trigger the [user] effect in App.tsx,
            // call refreshSubscription again, and create an infinite loop that
            // continuously cancels the fetchProjects effect before it completes.
            const planChanged = !status.error && user.plan !== status.plan;
            const categoryChanged = !status.error && status.userCategory && user.userCategory !== status.userCategory;
            if (planChanged || categoryChanged) {
                const updatedUser = {
                    ...user,
                    plan: status.plan,
                    ...(status.userCategory ? { userCategory: status.userCategory } : {}),
                };
                set({ user: updatedUser });
                localStorage.setItem('ost_user', JSON.stringify(updatedUser));
            }
        } catch (e) {
            console.warn('[refreshSubscription] Failed:', (e as Error).message);
        }
    },

    aiUsage: null,

    refreshAiUsage: async () => {
        const { user } = get();
        if (!user) return;
        try {
            const status = await fetchAiUsage();
            // Re-check after the await: the user may have logged out while the
            // request was in-flight (mirrors refreshSubscription's guard above).
            if (!get().user) return;
            // On error, keep the last known value rather than clobbering it with zeros —
            // a transient failure shouldn't make the badge briefly claim 0 credits used.
            if (status.error) return;
            set({
                aiUsage: {
                    creditsUsed: status.creditsUsed,
                    creditLimit: status.creditLimit,
                    period: status.period,
                    unlimited: status.unlimited,
                },
            });
        } catch (e) {
            console.warn('[refreshAiUsage] Failed:', (e as Error).message);
        }
    },
});
